import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { webSocketService } from './useWebSocket';
import { analyticsKeys } from './useAnalytics';

/**
 * Hook that listens for WebSocket events related to tickets, SLA,
 * and conversations, then invalidates analytics React Query caches
 * so dashboards and reports show near-real-time data.
 *
 * Mount this once in a top-level layout or the dashboard page.
 */
export function useAnalyticsRealtime() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Debounce invalidations to avoid excessive refetches when
    // multiple events fire in quick succession
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const invalidateAnalytics = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
      }, 2000); // 2s debounce
    };

    // Subscribe to ticket lifecycle events
    const unsubs = [
      webSocketService.subscribe('ticket:created', invalidateAnalytics),
      webSocketService.subscribe('ticket:updated', invalidateAnalytics),
      webSocketService.subscribe('ticket:assigned', invalidateAnalytics),
      webSocketService.subscribe('sla:warning', invalidateAnalytics),
    ];

    return () => {
      unsubs.forEach((unsub) => unsub());
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [queryClient]);
}
